import { useState, useEffect, useCallback } from 'react'
import {
  Save,
  RotateCcw,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Lock,
  Code2,
  LayoutList,
  X,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

type EditorMode = 'structured' | 'raw'

interface LarsConfigEditorProps {
  config: Record<string, any> | null
  isLoading?: boolean
  isSaving?: boolean
  saveError?: string | null
  saveSuccess?: boolean
  onSave: (config: Record<string, any>) => void
  onDismissSuccess?: () => void
  onDismissError?: () => void
}

// ─── Field-level helpers ──────────────────────────────────────────────────────

const KNOWN_ENUM_FIELDS: Record<string, string[]> = {
  selection_strategy: ['balanced', 'all', 'specific'],
  optimization_model: ['claude', 'gpt', 'gemini'],
  sdl_update_mode: ['add_optimized_field', 'replace_answer', 'append'],
}

const HIDDEN_KEY_PREFIXES = ['_comment', '_note', '_optimization']

function isHiddenKey(key: string): boolean {
  return HIDDEN_KEY_PREFIXES.some((p) => key.startsWith(p))
}

function isApiKeyField(key: string): boolean {
  return key === 'api_key'
}

// ─── Toggle component ─────────────────────────────────────────────────────────

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
        value ? 'bg-violet-500' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          value ? 'translate-x-4' : 'translate-x-1'
        }`}
      />
    </button>
  )
}

// ─── StringArray (tag chips) ──────────────────────────────────────────────────

function StringArrayField({
  value,
  onChange,
}: {
  value: string[]
  onChange: (v: string[]) => void
}) {
  const [inputVal, setInputVal] = useState('')

  const add = () => {
    const trimmed = inputVal.trim()
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed])
    }
    setInputVal('')
  }

  const remove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx))
  }

  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {value.map((item, idx) => (
        <span
          key={idx}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800"
        >
          {item}
          <button
            type="button"
            onClick={() => remove(idx)}
            className="text-violet-500 hover:text-violet-700 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="Add item…"
          className="w-28 px-2 py-0.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-400"
        />
        <button
          type="button"
          onClick={add}
          disabled={!inputVal.trim()}
          className="p-0.5 rounded text-violet-600 hover:text-violet-800 disabled:opacity-40 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

// ─── Models array editor ──────────────────────────────────────────────────────

function ModelCard({
  model,
  onChange,
  onRemove,
}: {
  model: Record<string, any>
  onChange: (updated: Record<string, any>) => void
  onRemove: () => void
}) {
  const [expanded, setExpanded] = useState(false)

  const update = (key: string, val: any) => onChange({ ...model, [key]: val })

  const editableKeys = Object.keys(model).filter(
    (k) => k !== 'platform' && k !== 'enabled' && !isHiddenKey(k) && !isApiKeyField(k)
  )

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-800 capitalize">{model.platform ?? 'unknown'}</span>
            {model.model && (
              <span className="text-xs text-gray-400 font-mono truncate">{model.model}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-500">{model.enabled ? 'Enabled' : 'Disabled'}</span>
          <Toggle value={!!model.enabled} onChange={(v) => update('enabled', v)} />
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="text-red-400 hover:text-red-600 transition-colors"
            title="Remove model"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-2.5 bg-gray-50/50">
          {editableKeys.map((key) => (
            <div key={key} className="flex items-center gap-3">
              <label className="w-36 flex-shrink-0 text-xs text-gray-500 font-mono">{key}</label>
              <input
                type="text"
                value={model[key] ?? ''}
                onChange={(e) => update(key, e.target.value)}
                className="flex-1 min-w-0 px-2.5 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white font-mono"
              />
            </div>
          ))}
          {/* api_key — always read-only */}
          {Object.keys(model).includes('api_key') && (
            <div className="flex items-center gap-3">
              <label className="w-36 flex-shrink-0 text-xs text-gray-500 font-mono">api_key</label>
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <input
                  type="text"
                  value=""
                  readOnly
                  placeholder="Managed via Key Vault"
                  className="flex-1 min-w-0 px-2.5 py-1 text-xs border border-gray-200 rounded-md bg-gray-100 text-gray-400 cursor-not-allowed font-mono"
                />
                <Lock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Object sub-section editor ────────────────────────────────────────────────

function ObjectField({
  label,
  value,
  onChange,
}: {
  label: string
  value: Record<string, any>
  onChange: (v: Record<string, any>) => void
}) {
  const [expanded, setExpanded] = useState(true)

  const update = (k: string, v: any) => onChange({ ...value, [k]: v })
  const remove = (k: string) => {
    const next = { ...value }
    delete next[k]
    onChange(next)
  }

  const [newKey, setNewKey] = useState('')
  const [newVal, setNewVal] = useState('')
  const [showAdd, setShowAdd] = useState(false)

  const addEntry = () => {
    const trimmedKey = newKey.trim()
    if (!trimmedKey) return
    const parsed = newVal === 'true' ? true : newVal === 'false' ? false : isNaN(Number(newVal)) ? newVal : Number(newVal)
    onChange({ ...value, [trimmedKey]: parsed })
    setNewKey('')
    setNewVal('')
    setShowAdd(false)
  }

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100/60 transition-colors"
      >
        {expanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
        <span className="text-xs font-semibold text-gray-700 font-mono">{label}</span>
        <span className="text-xs text-gray-400 ml-1">{'{…}'}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 pt-2">
              <span className="w-36 flex-shrink-0 text-xs text-gray-500 font-mono truncate">{k}</span>
              <input
                type={typeof v === 'number' ? 'number' : 'text'}
                value={v ?? ''}
                step={typeof v === 'number' && v <= 1 ? 0.01 : 1}
                onChange={(e) =>
                  update(k, typeof v === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)
                }
                className="flex-1 min-w-0 px-2.5 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
              />
              <button
                type="button"
                onClick={() => remove(k)}
                className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {showAdd ? (
            <div className="flex items-center gap-2 pt-2">
              <input
                type="text"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="key"
                className="w-28 px-2 py-1 text-xs border border-violet-300 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-400 font-mono"
              />
              <input
                type="text"
                value={newVal}
                onChange={(e) => setNewVal(e.target.value)}
                placeholder="value"
                className="flex-1 min-w-0 px-2 py-1 text-xs border border-violet-300 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-400"
              />
              <button type="button" onClick={addEntry} disabled={!newKey.trim()} className="text-violet-600 hover:text-violet-800 disabled:opacity-40">
                <CheckCircle2 className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="mt-2 flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 transition-colors"
            >
              <Plus className="h-3 w-3" />
              Add entry
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Single field row ─────────────────────────────────────────────────────────

function FieldRow({
  fieldKey,
  value,
  onChange,
  onRemove,
}: {
  fieldKey: string
  value: any
  onChange: (v: any) => void
  onRemove: () => void
}) {
  const enumOptions = KNOWN_ENUM_FIELDS[fieldKey]

  const input = (() => {
    if (typeof value === 'boolean') {
      return <Toggle value={value} onChange={onChange} />
    }
    if (enumOptions) {
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
        >
          {enumOptions.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      )
    }
    if (typeof value === 'number') {
      return (
        <input
          type="number"
          value={value}
          step={value <= 1 && value > 0 ? 0.01 : 1}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-32 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
        />
      )
    }
    if (typeof value === 'string') {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
        />
      )
    }
    return null
  })()

  return (
    <div className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
      <span className="w-52 flex-shrink-0 text-sm text-gray-600 font-mono truncate" title={fieldKey}>
        {fieldKey}
      </span>
      <div className="flex-1 min-w-0">{input}</div>
      <button
        type="button"
        onClick={onRemove}
        className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors"
        title="Remove field"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ─── Add Property panel ───────────────────────────────────────────────────────

type NewFieldType = 'string' | 'number' | 'boolean' | 'array' | 'object'

function AddPropertyRow({ onAdd }: { onAdd: (key: string, value: any) => void }) {
  const [open, setOpen] = useState(false)
  const [key, setKey] = useState('')
  const [type, setType] = useState<NewFieldType>('string')
  const [val, setVal] = useState('')

  const defaultForType = (t: NewFieldType): any => {
    switch (t) {
      case 'string': return ''
      case 'number': return 0
      case 'boolean': return false
      case 'array': return []
      case 'object': return {}
    }
  }

  const commit = () => {
    const trimmed = key.trim()
    if (!trimmed) return
    let finalVal: any
    if (type === 'string') finalVal = val
    else if (type === 'number') finalVal = parseFloat(val) || 0
    else if (type === 'boolean') finalVal = val === 'true'
    else finalVal = defaultForType(type)
    onAdd(trimmed, finalVal)
    setKey('')
    setVal('')
    setType('string')
    setOpen(false)
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-800 font-medium transition-colors"
      >
        <Plus className="h-4 w-4" />
        Add Property
      </button>
    )
  }

  return (
    <div className="mt-2 flex items-center gap-2 p-3 rounded-lg border border-violet-200 bg-violet-50/40">
      <input
        type="text"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="property_name"
        className="w-40 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 font-mono bg-white"
        autoFocus
      />
      <select
        value={type}
        onChange={(e) => setType(e.target.value as NewFieldType)}
        className="w-28 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
      >
        <option value="string">string</option>
        <option value="number">number</option>
        <option value="boolean">boolean</option>
        <option value="array">array</option>
        <option value="object">object</option>
      </select>
      {(type === 'string' || type === 'number') && (
        <input
          type={type === 'number' ? 'number' : 'text'}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="value"
          className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
        />
      )}
      {type === 'boolean' && (
        <select
          value={val || 'false'}
          onChange={(e) => setVal(e.target.value)}
          className="w-24 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      )}
      <button
        type="button"
        onClick={commit}
        disabled={!key.trim()}
        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
      >
        Add
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setKey(''); setVal('') }}
        className="text-gray-400 hover:text-gray-600 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

// ─── Structured editor body ───────────────────────────────────────────────────

function StructuredEditor({
  config,
  onChange,
}: {
  config: Record<string, any>
  onChange: (c: Record<string, any>) => void
}) {
  const set = (key: string, val: any) => onChange({ ...config, [key]: val })
  const remove = (key: string) => {
    const next = { ...config }
    delete next[key]
    onChange(next)
  }
  const add = (key: string, val: any) => onChange({ ...config, [key]: val })

  const entries = Object.entries(config).filter(([k]) => !isHiddenKey(k))

  return (
    <div className="space-y-1">
      {entries.map(([key, value]) => {
        // Models array
        if (key === 'models' && Array.isArray(value)) {
          return (
            <div key={key} className="py-2 border-b border-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700 font-mono">models</span>
                <button
                  type="button"
                  onClick={() =>
                    set('models', [
                      ...value,
                      { platform: 'new', model: '', api_key: '', enabled: false },
                    ])
                  }
                  className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add model
                </button>
              </div>
              <div className="space-y-2">
                {(value as Record<string, any>[]).map((model, idx) => (
                  <ModelCard
                    key={idx}
                    model={model}
                    onChange={(updated) => {
                      const next = [...value]
                      next[idx] = updated
                      set('models', next)
                    }}
                    onRemove={() => set('models', value.filter((_: any, i: number) => i !== idx))}
                  />
                ))}
              </div>
            </div>
          )
        }

        // String arrays
        if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
          return (
            <div key={key} className="flex items-start gap-3 py-2 border-b border-gray-50">
              <span className="w-52 flex-shrink-0 text-sm text-gray-600 font-mono pt-0.5">{key}</span>
              <div className="flex-1 min-w-0">
                <StringArrayField value={value} onChange={(v) => set(key, v)} />
              </div>
              <button
                type="button"
                onClick={() => remove(key)}
                className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors pt-0.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )
        }

        // Nested objects (e.g. scoring_weights)
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return (
            <div key={key} className="py-2 border-b border-gray-50">
              <ObjectField label={key} value={value} onChange={(v) => set(key, v)} />
            </div>
          )
        }

        // api_key at top level (shouldn't normally appear but just in case)
        if (isApiKeyField(key)) {
          return (
            <div key={key} className="flex items-center gap-3 py-2 border-b border-gray-50">
              <span className="w-52 flex-shrink-0 text-sm text-gray-600 font-mono">{key}</span>
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <input
                  type="text"
                  readOnly
                  placeholder="Managed via Key Vault"
                  className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed"
                />
                <Lock className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </div>
            </div>
          )
        }

        // Scalars
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return (
            <FieldRow
              key={key}
              fieldKey={key}
              value={value}
              onChange={(v) => set(key, v)}
              onRemove={() => remove(key)}
            />
          )
        }

        return null
      })}

      <AddPropertyRow onAdd={add} />
    </div>
  )
}

// ─── Raw JSON editor body ─────────────────────────────────────────────────────

function RawJsonEditor({
  jsonText,
  onChange,
  parseError,
}: {
  jsonText: string
  onChange: (t: string) => void
  parseError: string | null
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
        Editing raw JSON — all changes take effect on Save
      </div>
      <textarea
        value={jsonText}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className="w-full min-h-[400px] px-4 py-3 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 bg-gray-50 resize-y leading-relaxed"
      />
      <div className={`flex items-center gap-1.5 text-xs ${parseError ? 'text-red-600' : 'text-emerald-600'}`}>
        {parseError ? (
          <>
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            {parseError}
          </>
        ) : (
          <>
            <CheckCircle2 className="h-3.5 w-3.5" />
            Valid JSON
          </>
        )}
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LarsConfigEditor({
  config,
  isLoading = false,
  isSaving = false,
  saveError,
  saveSuccess,
  onSave,
  onDismissSuccess,
  onDismissError,
}: LarsConfigEditorProps) {
  const [mode, setMode] = useState<EditorMode>('structured')
  const [localConfig, setLocalConfig] = useState<Record<string, any>>({})
  const [rawJson, setRawJson] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  // Sync from parent config
  useEffect(() => {
    if (config) {
      setLocalConfig(config)
      setRawJson(JSON.stringify(config, null, 2))
      setIsDirty(false)
      setParseError(null)
    }
  }, [config])

  const handleStructuredChange = useCallback((updated: Record<string, any>) => {
    setLocalConfig(updated)
    setRawJson(JSON.stringify(updated, null, 2))
    setIsDirty(true)
  }, [])

  const handleRawChange = useCallback((text: string) => {
    setRawJson(text)
    setIsDirty(true)
    try {
      const parsed = JSON.parse(text)
      setLocalConfig(parsed)
      setParseError(null)
    } catch (e: any) {
      setParseError(`Invalid JSON: ${e.message}`)
    }
  }, [])

  const handleModeSwitch = (newMode: EditorMode) => {
    if (newMode === 'raw') {
      setRawJson(JSON.stringify(localConfig, null, 2))
      setParseError(null)
    } else {
      if (!parseError) {
        setLocalConfig(JSON.parse(rawJson))
      }
    }
    setMode(newMode)
  }

  const handleSave = () => {
    if (mode === 'raw') {
      try {
        const parsed = JSON.parse(rawJson)
        onSave(parsed)
      } catch {
        return
      }
    } else {
      onSave(localConfig)
    }
    setIsDirty(false)
  }

  const handleReset = () => {
    if (config) {
      setLocalConfig(config)
      setRawJson(JSON.stringify(config, null, 2))
      setIsDirty(false)
      setParseError(null)
    }
  }

  const canSave = isDirty && !isSaving && (mode === 'structured' || !parseError)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-violet-500 mr-2" />
        <span className="text-sm text-gray-500">Loading config…</span>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-gray-400">No config available.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">
          {Object.keys(localConfig).filter((k) => !isHiddenKey(k)).length} properties
        </p>
        <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden bg-white text-xs font-medium">
          <button
            type="button"
            onClick={() => handleModeSwitch('structured')}
            className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
              mode === 'structured' ? 'bg-violet-600 text-white' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <LayoutList className="h-3.5 w-3.5" />
            Structured
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch('raw')}
            className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
              mode === 'raw' ? 'bg-violet-600 text-white' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <Code2 className="h-3.5 w-3.5" />
            Raw JSON
          </button>
        </div>
      </div>

      {/* Save success banner */}
      {saveSuccess && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">Config saved successfully</span>
          {onDismissSuccess && (
            <button onClick={onDismissSuccess} className="text-emerald-500 hover:text-emerald-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Save error banner */}
      {saveError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{saveError}</span>
          {onDismissError && (
            <button onClick={onDismissError} className="text-red-400 hover:text-red-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Editor body */}
      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4">
        {mode === 'structured' ? (
          <StructuredEditor config={localConfig} onChange={handleStructuredChange} />
        ) : (
          <RawJsonEditor jsonText={rawJson} onChange={handleRawChange} parseError={parseError} />
        )}
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={handleReset}
          disabled={!isDirty || isSaving}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <RotateCcw className="h-4 w-4" />
          Reset to Saved
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {isSaving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  )
}
